import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { format, parseISO } from 'date-fns';

const styles = StyleSheet.create({
  page: {
    padding: 32,
    backgroundColor: '#FAFAF7',
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: '#37474F',
  },
  header: {
    fontSize: 20,
    textAlign: 'center',
    color: '#2F5E1E',
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    color: '#1B5E20',
    fontWeight: 'bold',
    marginBottom: 6,
    borderBottom: '1pt solid #4CAF50',
    paddingBottom: 4,
    textTransform: 'uppercase',
  },
  field: {
    marginBottom: 6,
  },
  label: {
    fontWeight: 'bold',
    color: '#263238',
    marginRight: 4,
  },
  signatureContainer: {
    marginTop: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  signatureBlock: {
    alignItems: 'center',
    width: '30%',
    marginBottom: 10,
  },
  signatureImage: {
    width: 100,
    height: 50,
    marginBottom: 4,
  },
  signatureLabel: {
    fontSize: 10,
    color: '#455A64',
  },
  attachmentsContainer: {
    marginTop: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  attachmentWrapper: {
    width: 160,
    height: 100,
    border: '1pt solid #BDBDBD',
    borderRadius: 4,
    overflow: 'hidden',
  },
  attachmentImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    textAlign: 'center',
    width: '100%',
    fontSize: 9,
    color: '#9E9E9E',
  },
});

const Field = ({ label, value }) => (
  <View style={styles.field}>
    <Text>
      <Text style={styles.label}>{label}:</Text> {value ? value : 'N/A'}
    </Text>
  </View>
);

const IncidentReportPDF = ({ reportData, base64Images = [] }) => {
  const formatDate = (dateStr) =>
    dateStr ? format(parseISO(dateStr), 'MM/dd/yyyy') : 'N/A';

  const safeBoolean = (value) => (value === true ? 'Yes' : 'No');

  // Fallback to imageUrls from reportData if base64Images are not passed
  const fallbackImages = Object.values(reportData.imageUrls || {})
    .filter(Boolean)
    .map((url) =>
      url.includes('firebasestorage.googleapis.com') && !url.includes('alt=media')
        ? `${url}?alt=media`
        : url
    );

  const allImages = base64Images?.length > 0 ? base64Images : fallbackImages;

  const chunkSize = 5;
  const imageChunks = [];
  for (let i = 0; i < allImages.length; i += chunkSize) {
    imageChunks.push(allImages.slice(i, i + chunkSize));
  }

  const renderSignatures = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Signatures</Text>
      <View style={styles.signatureContainer}>
        {reportData.driver?.signature && (
          <View style={styles.signatureBlock}>
            <Image style={styles.signatureImage} src={reportData.driver.signature} />
            <Text style={styles.signatureLabel}>Driver</Text>
          </View>
        )}
        {reportData.supervisor?.signature && (
          <View style={styles.signatureBlock}>
            <Image style={styles.signatureImage} src={reportData.supervisor.signature} />
            <Text style={styles.signatureLabel}>Supervisor</Text>
          </View>
        )}
        {reportData.passenger?.signature && (
          <View style={styles.signatureBlock}>
            <Image style={styles.signatureImage} src={reportData.passenger.signature} />
            <Text style={styles.signatureLabel}>Passenger</Text>
          </View>
        )}
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.header}>Incident Report</Text>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Employee Information</Text>
          <Field label="Name" value={reportData.employee?.name} />
          <Field label="Phone" value={reportData.employee?.phone} />
          <Field label="Job Title" value={reportData.employee?.jobTitle} />
          <Field label="Supervisor" value={reportData.supervisor?.name} />
          <Field label="Date of Incident" value={formatDate(reportData.occurrence?.injuryDate)} />
          <Field label="Time" value={reportData.occurrence?.accidentTime} />
          <Field label="Location" value={reportData.occurrence?.location} />
          <Field label="Truck Involved" value={reportData.vehicle?.truckNumber} />
          <Field label="Photos Attached?" value={safeBoolean(reportData.attachments?.picturesAttached)} />
          <Field label="Release Form Signed?" value={safeBoolean(reportData.attachments?.releaseFormSigned)} />
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Occurrence Details</Text>
          <Field label="Incident Description" value={reportData.supervisor?.report} />
          <Field label="Corrective Action" value={reportData.correctiveAction?.explanation} />
          <Field label="Employee Negligence?" value={safeBoolean(reportData.correctiveAction?.employeeNegligence)} />
          <Field label="Employee Deduction?" value={safeBoolean(reportData.correctiveAction?.deduction)} />
          <Field label="Reason (If No Deduction)" value={reportData.correctiveAction?.explanation} />
          <Field label="Witnesses Present?" value={reportData.witnesses?.length > 0 ? 'Yes' : 'No'} />
          {reportData.witnesses?.length > 0 &&
            reportData.witnesses
              .filter(w => w && w.trim() !== '')
              .map((w, i) => <Text key={i}>• {w}</Text>)}
          <Field label="Vehicle Damage" value={reportData.vehicle?.damageDescription} />
          <Field
            label="Claimant"
            value={
              reportData.vehicle?.claimant?.name?.trim()
                ? `${reportData.vehicle.claimant.name} - ${reportData.vehicle.claimant.phone || 'N/A'}`
                : 'N/A'
            }
          />
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Medical Details</Text>
          <Field label="Accompanied to Clinic?" value={safeBoolean(reportData.treatment?.accompanied)} />
          <Field label="Initial Treatment?" value={safeBoolean(reportData.treatment?.initialTreatment)} />
          <Field label="Denied Treatment?" value={safeBoolean(reportData.treatment?.denied)} />
          <Field label="Minor Clinic?" value={safeBoolean(reportData.treatment?.minorClinic)} />
          <Field label="Emergency?" value={safeBoolean(reportData.treatment?.emergency)} />
        </View>

        {renderSignatures()}

        <Text style={styles.footer}>
          {`Generated ${format(new Date(), 'Pp')} — Page 1`}
        </Text>
      </Page>

      {/* Render attached images in chunks */}
      {imageChunks.map((chunk, index) => (
        <Page key={`attachments-page-${index}`} size="A4" style={styles.page}>
          <Text style={styles.sectionTitle}>{`Attached Photos (Page ${index + 2})`}</Text>
          <View style={styles.attachmentsContainer}>
            {chunk.map((src, i) => (
              <View key={i} style={styles.attachmentWrapper}>
                <Image style={styles.attachmentImage} src={src} />
              </View>
            ))}
          </View>
          <Text style={styles.footer}>
            {`Generated ${format(new Date(), 'Pp')} — Page ${index + 2}`}
          </Text>
        </Page>
      ))}
    </Document>
  );
};

export default IncidentReportPDF;
